import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import {
  Users, ClipboardList, Truck, Package,
  TrendingUp, CalendarClock, ArrowRight,
  RefreshCw, UserX, Warehouse,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Status colour maps ─────────────────────────────── */
const LEAD_COLORS = {
  New: "#3B82F6", Contacted: "#F59E0B", Qualified: "#10B981",
  Converted: "#8B5CF6", Closed: "#94A3B8", Lost: "#EF4444", Invalid: "#64748B",
};
const LEAD_BADGES = {
  New:       "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-amber-50 text-amber-700 border-amber-200",
  Qualified: "bg-green-50 text-green-700 border-green-200",
  Converted: "bg-purple-50 text-purple-700 border-purple-200",
  Closed:    "bg-gray-100 text-gray-500 border-gray-200",
  Lost:      "bg-red-50 text-red-700 border-red-200",
  Invalid:   "bg-slate-100 text-slate-500 border-slate-200",
};
const LEAD_STATUSES = ["New", "Contacted", "Qualified", "Converted", "Closed", "Lost", "Invalid"];

const TRANSFER_COLORS = {
  Scheduled: "#3B82F6", "In Transit": "#F59E0B",
  Delivered: "#10B981", Cancelled: "#EF4444",
};
const TRANSFER_BADGES = {
  Scheduled:    "bg-blue-50 text-blue-700 border-blue-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  Delivered:    "bg-green-50 text-green-700 border-green-200",
  Cancelled:    "bg-red-50 text-red-700 border-red-200",
};

const CONSIGNMENT_COLORS = {
  Open: "#3B82F6", "In Transit": "#F59E0B",
  Completed: "#10B981", Cancelled: "#EF4444",
};
const CONSIGNMENT_BADGES = {
  Open:         "bg-blue-50 text-blue-700 border-blue-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  Completed:    "bg-green-50 text-green-700 border-green-200",
  Cancelled:    "bg-red-50 text-red-700 border-red-200",
};

const BOOKING_BADGES = {
  Active:    "bg-blue-50 text-blue-700 border-blue-200",
  Completed: "bg-green-50 text-green-700 border-green-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

/* ── Small reusable pieces ──────────────────────────── */
const StatusBadge = ({ status, badges }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${badges?.[status] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
    {status}
  </span>
);

const StatCard = ({ label, value, icon: Icon, color, loading, hint }) => (
  <div className="bg-white border border-[#E2E8F0] p-5 flex items-center justify-between" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">{label}</p>
      {loading
        ? <div className="mt-2 h-7 w-12 bg-[#F1F5F9] animate-pulse rounded" />
        : <p className="text-3xl font-bold text-[#1B2A6B] mt-1">{value ?? 0}</p>
      }
      {hint && <p className="text-[10px] text-[#94A3B8] mt-1">{hint}</p>}
    </div>
    <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: color }}>
      <Icon size={20} className="text-white" />
    </div>
  </div>
);

const CardHeader = ({ title, to, onNav }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">{title}</h3>
    {to && (
      <button onClick={onNav} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] hover:text-[#FFD700] transition-colors">
        View All <ArrowRight size={11} />
      </button>
    )}
  </div>
);

const EmptyState = ({ text }) => (
  <p className="text-sm text-[#94A3B8] text-center py-8">{text}</p>
);

const SkeletonRows = ({ rows = 4 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-9 bg-[#F1F5F9] animate-pulse rounded" />
    ))}
  </div>
);

/* Custom chart tooltips */
const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E2E8F0] px-3 py-2 shadow-sm text-xs rounded">
      <p className="font-bold text-[#0F172A] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E2E8F0] px-3 py-2 shadow-sm text-xs rounded">
      <p className="font-bold text-[#0F172A]">{payload[0].name}: {payload[0].value}</p>
    </div>
  );
};

/* ── Recent table ─────────────────────────────────────── */
const RecentTable = ({ headers, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[#F1F5F9]">
          {headers.map((h) => (
            <th key={h} className="text-left pb-2 text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] pr-3">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

/* ── Main Component ─────────────────────────────────── */
export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch {
      /* fail silently — show zeros */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const isCSM             = user?.role === "relationship_manager";
  const isWarehouseManager = user?.role === "warehouse_manager";
  const showLeads          = !isWarehouseManager;
  const showOps            = !isCSM;

  /* Lead pie data */
  const lbs = stats?.leads_by_status || {};
  const leadPieData = LEAD_STATUSES
    .filter((s) => (lbs[s] || 0) > 0)
    .map((s) => ({ name: s, value: lbs[s], color: LEAD_COLORS[s] }));
  const rmLeadActivity = stats?.rm_lead_activity || [];

  /* Transfer status horizontal bar data */
  const tbs = stats?.transfers_by_status || {};
  const transferBarData = Object.entries(tbs).map(([s, c]) => ({
    name: s, count: c, color: TRANSFER_COLORS[s] || "#94A3B8",
  }));

  /* Consignment status horizontal bar data */
  const cbs = stats?.consignments_by_status || {};
  const consignmentBarData = Object.entries(cbs).map(([s, c]) => ({
    name: s, count: c, color: CONSIGNMENT_COLORS[s] || "#94A3B8",
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">

      {/* ── Welcome banner ───────────────────────────── */}
      <div className="bg-[#1B2A6B] px-6 py-5 border-l-4 border-[#FFD700] flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">
            Welcome back, {user?.name || "User"}
          </h2>
          <p className="text-white/60 text-sm mt-0.5 capitalize">
            {user?.role?.replace(/_/g, " ")} · GoSmart Dashboard
          </p>
        </div>
        <button
          onClick={fetchStats}
          title="Refresh"
          className="text-white/40 hover:text-white transition-colors p-1"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── Stat cards ───────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {showLeads && (
          <>
            <StatCard label="Total Leads"     value={stats?.total_leads}      icon={Users}        color="#1B2A6B" loading={loading} />
            <StatCard label="Unassigned"       value={stats?.unassigned_leads} icon={UserX}        color="#EF4444" loading={loading} hint="Need assignment" />
            <StatCard label="Follow-ups Today" value={stats?.follow_ups_today} icon={CalendarClock} color="#F59E0B" loading={loading} hint="Shifting today" />
            <StatCard label="Converted"        value={stats?.converted_leads}  icon={TrendingUp}   color="#10B981" loading={loading} />
          </>
        )}
        {showOps && (
          <>
            <StatCard label="Total Confirmed Leads" value={stats?.total_bookings}     icon={ClipboardList} color="#2563EB" loading={loading} />
            <StatCard label="Total Consignments"    value={stats?.total_transfers}    icon={Truck}         color="#7C3AED" loading={loading} />
            <StatCard label="Trip Sheets"           value={stats?.total_consignments} icon={Package}       color="#0891B2" loading={loading} />
            <StatCard label="Warehouses"      value={stats?.warehouses}         icon={Warehouse}     color="#059669" loading={loading} />
          </>
        )}
      </div>

      {/* ── Monthly trend + Lead pipeline ────────────── */}
      {showLeads && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* Monthly trend bar chart */}
          <div className="lg:col-span-3 bg-white border border-[#E2E8F0] p-6" data-testid="monthly-trend-card">
            <CardHeader title="Monthly Activity — Last 3 Months" />
            {loading ? (
              <div className="h-44 bg-[#F1F5F9] animate-pulse rounded" />
            ) : (
              <ResponsiveContainer width="100%" height={185}>
                <BarChart data={stats?.monthly_trend || []} barCategoryGap="30%" barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={24} />
                  <Tooltip content={<BarTooltip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: "#475569", paddingTop: 8 }} />
                  <Bar dataKey="leads"    name="Leads"    fill="#1B2A6B" radius={[3,3,0,0]} maxBarSize={40} />
                  <Bar dataKey="bookings" name="Bookings" fill="#FFD700" radius={[3,3,0,0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Lead pipeline donut */}
          <div className="lg:col-span-2 bg-white border border-[#E2E8F0] p-6" data-testid="lead-pipeline-card">
            <CardHeader title="Lead Pipeline" to="/leads" onNav={() => navigate("/leads")} />
            {loading ? (
              <div className="h-44 bg-[#F1F5F9] animate-pulse rounded" />
            ) : leadPieData.length === 0 ? (
              <EmptyState text="No leads yet" />
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={leadPieData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value">
                      {leadPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-1 space-y-1.5">
                  {leadPieData.map((entry) => (
                    <div key={entry.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                        <span className="text-[#475569]">{entry.name}</span>
                      </div>
                      <span className="font-bold text-[#0F172A]">{entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showLeads && (
        <div className="bg-white border border-[#E2E8F0] p-6" data-testid="rm-lead-activity-card">
          <CardHeader title="RM Lead Assignment & Follow-up Coverage" />
          <p className="text-[11px] text-[#94A3B8] -mt-2 mb-4">Includes Open, Contacted, and Qualified leads only. Followed up means at least one follow-up was added in the last 2 days.</p>
          {loading ? (
            <div className="h-52 bg-[#F1F5F9] animate-pulse rounded" />
          ) : rmLeadActivity.length === 0 ? (
            <EmptyState text="No Relationship Managers found" />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, rmLeadActivity.length * 62)}>
              <BarChart layout="vertical" data={rmLeadActivity} margin={{ left: 10, right: 24, top: 4, bottom: 4 }} barCategoryGap="24%" barGap={3}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="rm_name" tick={{ fontSize: 11, fill: "#475569" }} width={130} axisLine={false} tickLine={false} />
                <Tooltip content={<BarTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: "#475569", paddingTop: 8 }} />
                <Bar dataKey="total_assigned" name="Total Assigned Leads" fill="#1B2A6B" radius={[0, 3, 3, 0]} maxBarSize={18} />
                <Bar dataKey="followed_up" name="Followed Up - Last 2 Days" fill="#10B981" radius={[0, 3, 3, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* ── Ops status charts ────────────────────────── */}
      {showOps && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Transfers by status */}
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="transfers-status-card">
            <CardHeader title="Consignments by Status" to="/consignments" onNav={() => navigate("/consignments")} />
            {loading ? (
              <div className="h-28 bg-[#F1F5F9] animate-pulse rounded" />
            ) : transferBarData.length === 0 ? (
              <EmptyState text="No transfers yet" />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(100, transferBarData.length * 36)}>
                <BarChart layout="vertical" data={transferBarData} margin={{ left: 0, right: 20, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} width={76} axisLine={false} tickLine={false} />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="count" name="Transfers" radius={[0,3,3,0]} maxBarSize={20}>
                    {transferBarData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Consignments by status */}
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="consignments-status-card">
            <CardHeader title="Trip Sheets by Status" to="/trips" onNav={() => navigate("/trips")} />
            {loading ? (
              <div className="h-28 bg-[#F1F5F9] animate-pulse rounded" />
            ) : consignmentBarData.length === 0 ? (
              <EmptyState text="No consignments yet" />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(100, consignmentBarData.length * 36)}>
                <BarChart layout="vertical" data={consignmentBarData} margin={{ left: 0, right: 20, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} width={76} axisLine={false} tickLine={false} />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="count" name="Consignments" radius={[0,3,3,0]} maxBarSize={20}>
                    {consignmentBarData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

        </div>
      )}

      {/* ── Recent: Leads + Bookings ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Recent Leads */}
        {showLeads && (
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="recent-leads-card">
            <CardHeader title="Recent Leads" to="/leads" onNav={() => navigate("/leads")} />
            {loading ? <SkeletonRows /> : !stats?.recent_leads?.length ? <EmptyState text="No leads yet" /> : (
              <RecentTable
                headers={["Customer", "Route", "Date", "Status"]}
                rows={stats.recent_leads.map((lead, idx) => (
                  <tr key={lead.id} data-testid={`recent-lead-${idx}`}
                    className="border-b border-[#F9FAFB] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                    onClick={() => navigate(`/leads/${lead.id}`)}>
                    <td className="py-2.5 pr-3 font-semibold text-[#0F172A] whitespace-nowrap text-xs">{lead.customer_name}</td>
                    <td className="py-2.5 pr-3 text-[#475569] whitespace-nowrap text-[11px]">{lead.moving_from} → {lead.moving_to}</td>
                    <td className="py-2.5 pr-3 text-[#475569] whitespace-nowrap text-[11px]">
                      {lead.shifting_date ? format(new Date(lead.shifting_date + "T00:00:00"), "dd MMM yy") : "—"}
                    </td>
                    <td className="py-2.5"><StatusBadge status={lead.status} badges={LEAD_BADGES} /></td>
                  </tr>
                ))}
              />
            )}
          </div>
        )}

        {/* Recent Bookings */}
        {showOps && (
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="recent-bookings-card">
            <CardHeader title="Recent Confirmed Leads" to="/confirmed-leads" onNav={() => navigate("/confirmed-leads")} />
            {loading ? <SkeletonRows /> : !stats?.recent_bookings?.length ? <EmptyState text="No bookings yet" /> : (
              <RecentTable
                headers={["Customer", "Route", "Date", "Status"]}
                rows={stats.recent_bookings.map((b, idx) => (
                  <tr key={b.id} data-testid={`recent-booking-${idx}`}
                    className="border-b border-[#F9FAFB] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                    onClick={() => navigate(`/confirmed-leads/${b.id}`)}>
                    <td className="py-2.5 pr-3 font-semibold text-[#0F172A] whitespace-nowrap text-xs">{b.customer_name}</td>
                    <td className="py-2.5 pr-3 text-[#475569] whitespace-nowrap text-[11px]">{b.moving_from} → {b.moving_to}</td>
                    <td className="py-2.5 pr-3 text-[#475569] whitespace-nowrap text-[11px]">
                      {b.shifting_date ? format(new Date(b.shifting_date + "T00:00:00"), "dd MMM yy") : "—"}
                    </td>
                    <td className="py-2.5"><StatusBadge status={b.status} badges={BOOKING_BADGES} /></td>
                  </tr>
                ))}
              />
            )}
          </div>
        )}

      </div>

      {/* ── Recent: Consignments + Trips ─────────── */}
      {showOps && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Recent Consignments */}
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="recent-transfers-card">
            <CardHeader title="Recent Consignments" to="/consignments" onNav={() => navigate("/consignments")} />
            {loading ? <SkeletonRows rows={3} /> : !stats?.recent_transfers?.length ? <EmptyState text="No consignments yet" /> : (
              <RecentTable
                headers={["LR No.", "Customer", "Route", "Status"]}
                rows={stats.recent_transfers.map((t, idx) => (
                  <tr key={t.id} data-testid={`recent-transfer-${idx}`}
                    className="border-b border-[#F9FAFB] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-2.5 pr-3 font-mono text-[11px] font-bold text-[#1B2A6B] whitespace-nowrap">{t.lr_number || "—"}</td>
                    <td className="py-2.5 pr-3 text-[#0F172A] whitespace-nowrap text-xs">{t.customer_name || "—"}</td>
                    <td className="py-2.5 pr-3 text-[#475569] whitespace-nowrap text-[11px]">{t.origin_label} → {t.destination_label}</td>
                    <td className="py-2.5"><StatusBadge status={t.status} badges={TRANSFER_BADGES} /></td>
                  </tr>
                ))}
              />
            )}
          </div>

          {/* Recent Trips */}
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="recent-consignments-card">
            <CardHeader title="Recent Trip Sheets" to="/trips" onNav={() => navigate("/trips")} />
            {loading ? <SkeletonRows rows={3} /> : !stats?.recent_consignments?.length ? <EmptyState text="No trip sheets yet" /> : (
              <RecentTable
                headers={["CON No.", "Vehicle", "Date", "Status"]}
                rows={stats.recent_consignments.map((c, idx) => (
                  <tr key={c.id} data-testid={`recent-consignment-${idx}`}
                    className="border-b border-[#F9FAFB] hover:bg-[#F9FAFB] transition-colors">
                    <td className="py-2.5 pr-3 font-mono text-[11px] font-bold text-[#1B2A6B] whitespace-nowrap">{c.consignment_number || "—"}</td>
                    <td className="py-2.5 pr-3 text-[#0F172A] whitespace-nowrap text-xs">{c.vehicle_display || "—"}</td>
                    <td className="py-2.5 pr-3 text-[#475569] whitespace-nowrap text-[11px]">
                      {c.consignment_date ? format(new Date(c.consignment_date + "T00:00:00"), "dd MMM yy") : "—"}
                    </td>
                    <td className="py-2.5"><StatusBadge status={c.status} badges={CONSIGNMENT_BADGES} /></td>
                  </tr>
                ))}
              />
            )}
          </div>

        </div>
      )}

    </div>
  );
}
